import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import { Button, Row, Table, Input, Label, ButtonGroup, Col } from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import moment from "moment";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Checkbox from "@material-ui/core/Checkbox";

// (2026-08) Control y consumo de telas: por renglón de orden de compra, lo
// comprado contra lo recibido (con la factura del proveedor), lo que ya se usó
// y el saldo que queda en almacén.
function ReporteTelas() {
  const { user } = useContext(AuthContext);
  const URL_TELAS_OC = process.env.REACT_APP_URL_TELAS_OC;

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 90);

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(
    moment(inicio).format("YYYY-MM-DD")
  );
  const [selectedFechaFin, setSelectedFechaFin] = useState(
    moment(hoy).format("YYYY-MM-DD")
  );

  const [renglones, setRenglones] = useState([]);
  // (2026-09) varios proveedores a la vez
  const [selectedProveedores, setSelectedProveedores] = useState([]);
  const [soloRecibidas, setSoloRecibidas] = useState("Si");
  const [cargando, setCargando] = useState(false);

  const headers = {
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  };

  const arr = (d) => (Array.isArray(d) ? d : []);
  const mts = (n) =>
    `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
      Number(n || 0)
    )} m`;
  const money = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));
  const pct = (parte, total) =>
    Number(total || 0) > 0 ? (Number(parte || 0) / Number(total)) * 100 : 0;

  // (2026-09) número grande y en negritas en los recuadros del resumen
  const estiloKpi = {
    fontSize: "1.7rem",
    fontWeight: 700,
    lineHeight: 1.15,
    margin: "2px 0",
  };

  useEffect(() => {
    setCargando(true);
    axios
      .get(`${URL_TELAS_OC}Reporte/${selectedFechaInicio}/${selectedFechaFin}`, {
        headers,
      })
      .then((r) => {
        setRenglones(
          arr(r.data).sort((a, b) => Number(b.oc) - Number(a.oc))
        );
        setCargando(false);
      })
      .catch((e) => {
        console.log(e);
        setCargando(false);
        Swal.fire("Error", "No se pudo cargar el reporte de telas", "error");
      });
  }, [selectedFechaInicio, selectedFechaFin]);

  // (2026-09) Los proveedores del combo salen de las órdenes de compra de TELA
  // del periodo: así sólo se listan los proveedores de tela y no el catálogo
  // completo (que trae también habilitación, maquila, servicios, etc.).
  const proveedoresTela = useMemo(() => {
    const m = new Map();
    renglones.forEach((r) => {
      const id = String(r.idProveedor || "");
      if (!id) return;
      const nombre = String(r.proveedor || "").trim() || id;
      if (!m.has(id)) m.set(id, { _id: id, nombre });
    });
    return [...m.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }, [renglones]);

  // si al cambiar las fechas alguno ya no existe, se quita de la selección
  useEffect(() => {
    setSelectedProveedores((prev) =>
      prev.filter((p) => proveedoresTela.some((x) => x._id === p._id))
    );
  }, [proveedoresTela]);

  const filtrados = useMemo(() => {
    const ids = selectedProveedores.map((p) => String(p._id));
    return renglones.filter(
      (r) =>
        (ids.length === 0 || ids.includes(String(r.idProveedor))) &&
        (soloRecibidas !== "Si" || Number(r.mtsRecibidos || 0) > 0)
    );
  }, [renglones, selectedProveedores, soloRecibidas]);

  const tot = useMemo(() => {
    const t = {
      comprados: 0,
      recibidos: 0,
      usados: 0,
      saldo: 0,
      subtotalRecibido: 0,
      subtotalComprado: 0,
    };
    filtrados.forEach((r) => {
      t.comprados += Number(r.mtsComprados || 0);
      t.recibidos += Number(r.mtsRecibidos || 0);
      t.usados += Number(r.mtsUsados || 0);
      t.saldo += Number(r.saldoAlmacen || 0);
      t.subtotalRecibido += Number(r.subtotalRecibido || 0);
      t.subtotalComprado += Number(r.subtotalComprado || 0);
    });
    t.ordenes = new Set(filtrados.map((r) => r.oc)).size;
    return t;
  }, [filtrados]);

  // ---------------- PDF ----------------
  const PDFReporte = () => {
    if (!filtrados.length) {
      return Swal.fire("Sin datos", "No hay telas en el periodo/filtros", "warning");
    }

    const doc = new jsPDF("landscape", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const ML = 10;
    const usableW = pageW - ML * 2;

    const azulMarino = [17, 32, 74];
    const azul = [46, 117, 182];
    const verde = [39, 124, 70];
    const verdeClaro = [235, 247, 239];
    const gris = [110, 120, 135];
    const bordeGris = [210, 216, 224];

    // ---- Banda de título
    doc.setFillColor(...azulMarino);
    doc.roundedRect(ML, 8, usableW, 18, 2, 2, "F");
    doc.setTextColor(255).setFont(undefined, "bold").setFontSize(15);
    doc.text("LANGIANI — CONTROL Y CONSUMO DE TELAS", ML + 5, 16);
    doc.setFont(undefined, "normal").setFontSize(8.5);
    doc.text(
      `Catálogo Técnico con Facturación Real y Subtotales por Metro Recibido | Periodo: ${moment(
        selectedFechaInicio
      ).format("DD/MM/YYYY")} - ${moment(selectedFechaFin).format("DD/MM/YYYY")}`,
      ML + 5,
      21.5
    );
    doc.setTextColor(0);

    // ---- Tarjetas
    let y = 30;
    const tarjetas = [
      {
        titulo: "MTS COMPRADOS",
        valor: mts(tot.comprados),
        pie: `${tot.ordenes} Órdenes de Compra`,
      },
      {
        titulo: "MTS RECIBIDOS",
        valor: mts(tot.recibidos),
        pie: `${pct(tot.recibidos, tot.comprados).toFixed(1)}% Recepción Real`,
      },
      {
        titulo: "SUBTOTAL RECIBIDO (REAL)",
        valor: money(tot.subtotalRecibido),
        pie: "Basado en Mts Recibidos",
        resaltado: true,
      },
      {
        titulo: "MTS UTILIZADOS",
        valor: mts(tot.usados),
        pie: `${pct(tot.usados, tot.recibidos).toFixed(1)}% Uso / Recibido`,
      },
      {
        titulo: "SALDO ALMACÉN",
        valor: mts(tot.saldo),
        pie: "Disponible Confección",
      },
      {
        titulo: "SUBTOTAL COMPRADO",
        valor: money(tot.subtotalComprado),
        pie: `Diferencia: ${money(tot.subtotalRecibido - tot.subtotalComprado)}`,
      },
    ];

    const gap = 3;
    const tarjW = (usableW - gap * (tarjetas.length - 1)) / tarjetas.length;
    const tarjH = 19;
    tarjetas.forEach((t, i) => {
      const x = ML + i * (tarjW + gap);
      doc.setFillColor(...(t.resaltado ? [232, 240, 250] : [255, 255, 255]));
      doc.setDrawColor(...(t.resaltado ? azul : bordeGris)).setLineWidth(0.25);
      doc.roundedRect(x, y, tarjW, tarjH, 1.5, 1.5, "FD");
      doc.setFont(undefined, "bold").setFontSize(6.8).setTextColor(...gris);
      doc.text(t.titulo, x + tarjW / 2, y + 5, { align: "center" });
      doc.setFontSize(11).setTextColor(...(t.resaltado ? azul : azulMarino));
      doc.text(t.valor, x + tarjW / 2, y + 11.5, { align: "center" });
      doc.setFont(undefined, "normal").setFontSize(6.5).setTextColor(...gris);
      doc.text(t.pie, x + tarjW / 2, y + 16, { align: "center" });
      doc.setTextColor(0);
    });

    y += tarjH + 5;

    // ---- Banda de sección
    doc.setFillColor(...azulMarino);
    doc.rect(ML, y, usableW, 7, "F");
    doc.setFont(undefined, "bold").setFontSize(9.5).setTextColor(255);
    doc.text(
      "1. DETALLE CON NÚMERO DE FACTURA, METROS RECIBIDOS Y SUBTOTAL RECIBIDO",
      ML + 3,
      y + 4.8
    );
    doc.setTextColor(0);
    y += 7;

    doc.autoTable({
      startY: y,
      margin: { left: ML, right: ML, top: 12, bottom: 14 },
      tableWidth: usableW,
      theme: "grid",
      styles: {
        fontSize: 7.5,
        cellPadding: 1.5,
        valign: "middle",
        lineColor: bordeGris,
        lineWidth: 0.15,
        textColor: 40,
      },
      headStyles: {
        fillColor: [240, 243, 247],
        textColor: azulMarino,
        fontStyle: "bold",
        fontSize: 7,
        halign: "center",
      },
      alternateRowStyles: { fillColor: [250, 251, 253] },
      head: [[
        "OC", "NO. FACTURA", "NOMBRE DE TELA", "EMPRESA PROVEEDORA",
        "MTS COMP.", "MTS REC.", "PRECIO/M", "SUBTOTAL RECIBIDO ($)",
        "MTS USADOS", "SALDO ALM.",
      ]],
      columnStyles: {
        0: { cellWidth: 12, halign: "center", fontStyle: "bold" },
        1: { cellWidth: 28, halign: "center" },
        2: { cellWidth: 58, fontStyle: "bold" },
        3: { cellWidth: 52 },
        4: { cellWidth: 20, halign: "right" },
        5: { cellWidth: 20, halign: "right", fontStyle: "bold" },
        6: { cellWidth: 20, halign: "right" },
        7: { cellWidth: 30, halign: "right", fontStyle: "bold" },
        8: { halign: "right" },
        9: { halign: "right" },
      },
      body: filtrados.map((r) => [
        String(r.oc),
        r.factura || "-",
        [r.tela, r.color].filter((x) => x).join(" / "),
        r.proveedor || "-",
        mts(r.mtsComprados),
        mts(r.mtsRecibidos),
        money(r.precio),
        money(r.subtotalRecibido),
        mts(r.mtsUsados),
        mts(r.saldoAlmacen),
      ]),
      foot: [[
        { content: "TOTALES GENERALES:", colSpan: 4, styles: { halign: "right" } },
        mts(tot.comprados),
        mts(tot.recibidos),
        "-",
        money(tot.subtotalRecibido),
        mts(tot.usados),
        mts(tot.saldo),
      ]],
      footStyles: {
        fillColor: azulMarino,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "right",
      },
      didParseCell: (h) => {
        // La columna del subtotal recibido va resaltada, como en el original
        if (h.column.index === 7 && h.section === "body") {
          h.cell.styles.fillColor = verdeClaro;
          h.cell.styles.textColor = verde;
        }
      },
    });

    // ---- Nota
    let yNota = (doc.lastAutoTable?.finalY || y) + 5;
    if (yNota + 16 > pageH - 12) {
      doc.addPage();
      yNota = 16;
    }
    doc.setFillColor(255, 250, 235).setDrawColor(230, 180, 60).setLineWidth(0.3);
    doc.rect(ML, yNota, usableW, 13, "FD");
    doc.setFillColor(230, 160, 30);
    doc.rect(ML, yNota, 1.5, 13, "F");
    doc.setFont(undefined, "bold").setFontSize(8).setTextColor(120, 80, 10);
    doc.text("Nota:", ML + 4, yNota + 5);
    doc.setFont(undefined, "normal").setFontSize(7.5).setTextColor(80, 60, 20);
    doc.text(
      "El subtotal se calcula con los metros RECIBIDOS (Mts. Recibidos x Precio/m), no con los comprados. Los metros usados salen de lo",
      ML + 16,
      yNota + 5
    );
    doc.text(
      "recibido menos el saldo que queda en almacén, y la factura es la del proveedor capturada al recibir la orden de compra.",
      ML + 16,
      yNota + 9.5
    );
    doc.setTextColor(0);

    // ---- Pie
    const paginas = doc.internal.getNumberOfPages();
    for (let p = 1; p <= paginas; p++) {
      doc.setPage(p);
      doc.setDrawColor(...bordeGris).setLineWidth(0.2);
      doc.line(ML, pageH - 10, pageW - ML, pageH - 10);
      doc.setFont(undefined, "normal").setFontSize(7.5).setTextColor(...gris);
      doc.text("LANGIANI ERP - Control y Consumo de Telas", ML, pageH - 5.5);
      doc.text(`Página ${p} de ${paginas}`, pageW - ML, pageH - 5.5, { align: "right" });
      doc.setTextColor(0);
    }

    window.open(doc.output("bloburl"), "_blank");
  };

  const excel = () => {
    if (!filtrados.length) {
      return Swal.fire("Sin datos", "No hay telas en el periodo/filtros", "warning");
    }
    const option = {};
    option.fileName = "ReporteTelas";
    option.datas = [
      {
        sheetData: filtrados.map((r) => ({
          OC: r.oc,
          Factura: r.factura,
          Tela: r.tela,
          Color: r.color,
          Proveedor: r.proveedor,
          MtsComprados: r.mtsComprados,
          MtsRecibidos: r.mtsRecibidos,
          Precio: r.precio,
          SubtotalRecibido: r.subtotalRecibido,
          MtsUsados: r.mtsUsados,
          SaldoAlmacen: r.saldoAlmacen,
        })),
        sheetName: "Telas",
        sheetFilter: ["OC","Factura","Tela","Color","Proveedor","MtsComprados","MtsRecibidos","Precio","SubtotalRecibido","MtsUsados","SaldoAlmacen"],
        sheetHeader: ["OC","No. Factura","Tela","Color","Empresa Proveedora","Mts Comprados","Mts Recibidos","Precio/m","Subtotal Recibido","Mts Usados","Saldo Almacén"],
      },
    ];
    new ExportJsonExcel(option).saveExcel();
  };

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_produccion || user.menu_compras ? (
        <div className="card container col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuCompras" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFReporte}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Control y Consumo de Telas</h3>

          <Row>
            <Col md={2}>
              <Label>Fecha Inicio</Label>
              <Input
                // bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha Fin</Label>
              <Input
                // bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Label>Proveedor</Label>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={proveedoresTela}
                value={selectedProveedores}
                getOptionLabel={(o) => o?.nombre || ""}
                getOptionSelected={(o, v) => String(o._id) === String(v._id)}
                onChange={(e, val) => setSelectedProveedores(val || [])}
                renderOption={(option, { selected }) => (
                  <>
                    <Checkbox size="small" checked={selected} color="primary" />
                    {option.nombre}
                  </>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={
                      selectedProveedores.length ? "" : "Todos los proveedores"
                    }
                  />
                )}
              />
            </Col>
            <Col md={2}>
              <Label>Solo recibidas</Label>
              <Input
                // bsSize="sm"
                type="select"
                value={soloRecibidas}
                onChange={(e) => setSoloRecibidas(e.target.value)}
              >
                <option value="Si">Si</option>
                <option value="No">No (todas)</option>
              </Input>
            </Col>
          </Row>

          <br />

          {/* (2026-09) Resumen: el número es lo que se lee de un vistazo */}
          <Row>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">MTS COMPRADOS</small>
                <div style={estiloKpi}>{mts(tot.comprados)}</div>
                <small className="text-muted">{tot.ordenes} Órdenes de Compra</small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">MTS RECIBIDOS</small>
                <div style={estiloKpi}>{mts(tot.recibidos)}</div>
                <small className="text-muted">
                  {pct(tot.recibidos, tot.comprados).toFixed(1)}% Recepción
                </small>
              </div>
            </Col>
            <Col md>
              <div
                className="card p-2 text-center"
                style={{ borderTop: "3px solid #2E75B6" }}
              >
                <small className="text-muted">SUBTOTAL RECIBIDO</small>
                <div style={{ ...estiloKpi, color: "#2E75B6" }}>
                  {money(tot.subtotalRecibido)}
                </div>
                <small className="text-muted">Basado en Mts Recibidos</small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">MTS UTILIZADOS</small>
                <div style={estiloKpi}>{mts(tot.usados)}</div>
                <small className="text-muted">
                  {pct(tot.usados, tot.recibidos).toFixed(1)}% Uso / Recibido
                </small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">SALDO ALMACÉN</small>
                <div style={estiloKpi}>{mts(tot.saldo)}</div>
                <small className="text-muted">Disponible Confección</small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">SUBTOTAL COMPRADO</small>
                <div style={estiloKpi}>{money(tot.subtotalComprado)}</div>
                <small className="text-muted">
                  Diferencia: {money(tot.subtotalRecibido - tot.subtotalComprado)}
                </small>
              </div>
            </Col>
          </Row>

          <br />
          {cargando ? <h5 align="center">Cargando...</h5> : null}

          <Table size="sm" striped borderless className="table-responsive-xl">
            <thead>
              <tr>
                <th className="text-center">OC</th>
                <th className="text-center">No. Factura</th>
                <th>Nombre de Tela</th>
                <th>Empresa Proveedora</th>
                <th className="text-end">Mts Comp.</th>
                <th className="text-end">Mts Rec.</th>
                <th className="text-end">Precio/m</th>
                <th className="text-end">Subtotal Recibido</th>
                <th className="text-end">Mts Usados</th>
                <th className="text-end">Saldo Alm.</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length ? (
                filtrados.map((r) => (
                  <tr key={r._id}>
                    <td className="text-center negrita">{r.oc}</td>
                    <td className="text-center">{r.factura || "-"}</td>
                    <td className="negrita">
                      {[r.tela, r.color].filter((x) => x).join(" / ")}
                    </td>
                    <td>{r.proveedor}</td>
                    <td className="text-end">{mts(r.mtsComprados)}</td>
                    <td className="text-end negrita">{mts(r.mtsRecibidos)}</td>
                    <td className="text-end">{money(r.precio)}</td>
                    <td className="text-end negrita" style={{ color: "#277C46" }}>
                      {money(r.subtotalRecibido)}
                    </td>
                    <td className="text-end">{mts(r.mtsUsados)}</td>
                    <td className="text-end">{mts(r.saldoAlmacen)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center text-muted">
                    No hay telas en el periodo seleccionado
                  </td>
                </tr>
              )}
              {filtrados.length ? (
                <tr>
                  <td colSpan="4" className="text-end negrita">
                    TOTALES GENERALES:
                  </td>
                  <td className="text-end negrita">{mts(tot.comprados)}</td>
                  <td className="text-end negrita">{mts(tot.recibidos)}</td>
                  <td></td>
                  <td className="text-end negrita">{money(tot.subtotalRecibido)}</td>
                  <td className="text-end negrita">{mts(tot.usados)}</td>
                  <td className="text-end negrita">{mts(tot.saldo)}</td>
                </tr>
              ) : null}
            </tbody>
          </Table>
          <br />
        </div>
      ) : undefined}
      <br />
    </>
  );
}

export default ReporteTelas;
